import React, { useEffect, useMemo, useState, Children } from 'react';
import { GitBranch, Folder, FolderOpen, FileText, AlertTriangle, ChevronRight, ChevronDown, Layers, Hash, Tag, CheckCircle, AlertOctagon, BoxIcon, Copy, BookOpen, Link2Off, Search } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Question, ModuleId } from '../../types';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { MODULES } from '../../data/modules';
import { HINT_REGISTRY } from '../../data/hints';
interface SkillTreePanelProps {
  questions: Question[];
}
type TreeNode = {
  id: string;
  label: string;
  type: 'module' | 'skill' | 'subSkill' | 'family' | 'question';
  count: number;
  children?: TreeNode[];
  data?: Question; // Only for 'question' type
  issues?: string[];
};
type SimilarSkillGroup = {
  name: string;
  modules: string[];
  count: number;
};
type HintCoverage = {
  moduleId: string;
  subSkill: string;
  hasHint: boolean;
  questionCount: number;
};
export function SkillTreePanel({
  questions
}: SkillTreePanelProps) {
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [expandedIssues, setExpandedIssues] = useState<Set<string>>(new Set(['unlinked', 'duplicates', 'hints']));
  // Process data into hierarchy and stats
  const {
    tree,
    stats,
    unlinkedQuestions,
    issueMap,
    duplicateSkills,
    similarSkills,
    hintCoverage,
    orphanedHints
  } = useMemo(() => {
    const root: TreeNode[] = [];
    const unlinked: Question[] = [];
    const issueMap = new Map<string, string[]>();
    const stats = {
      modules: new Set<string>(),
      skills: new Set<string>(),
      subSkills: new Set<string>(),
      families: new Set<string>()
    };
    // Tracking for duplicates/similarities
    const subSkillMap = new Map<string, Set<string>>(); // subSkill -> Set<ModuleId>
    const subSkillCounts = new Map<string, number>();
    // Helper to find or create node
    const getOrCreateNode = (parentChildren: TreeNode[], id: string, label: string, type: TreeNode['type']) => {
      let node = parentChildren.find(n => n.id === id);
      if (!node) {
        node = {
          id,
          label,
          type,
          count: 0,
          children: []
        };
        parentChildren.push(node);
      }
      return node;
    };
    questions.forEach(q => {
      // Check for unlinked/issues
      const issues: string[] = [];
      if (!q.module) issues.push('Missing Module');
      if (!q.skill) issues.push('Missing Skill');
      if (!q.subSkill) issues.push('Missing SubSkill');
      if (!q.familyId) issues.push('Missing FamilyId');
      if (issues.length > 0) {
        unlinked.push(q);
        issueMap.set(q.id, issues);
      }
      // Update stats
      if (q.module) stats.modules.add(q.module);
      if (q.skill) stats.skills.add(q.skill);
      if (q.subSkill) {
        stats.subSkills.add(q.subSkill);
        // Track for duplicates
        if (q.module) {
          if (!subSkillMap.has(q.subSkill)) {
            subSkillMap.set(q.subSkill, new Set());
          }
          subSkillMap.get(q.subSkill)!.add(q.module);
        }
        subSkillCounts.set(q.subSkill, (subSkillCounts.get(q.subSkill) || 0) + 1);
      }
      if (q.familyId) stats.families.add(q.familyId);
      // Build Tree
      // Level 1: Module
      const modId = q.module || 'UNKNOWN_MODULE';
      const modNode = getOrCreateNode(root, `mod-${modId}`, modId, 'module');
      modNode.count++;
      // Level 2: Skill
      const skillId = q.skill || 'UNKNOWN_SKILL';
      const skillNode = getOrCreateNode(modNode.children!, `skill-${modId}-${skillId}`, skillId, 'skill');
      skillNode.count++;
      // Level 3: SubSkill
      const subId = q.subSkill || 'UNKNOWN_SUBSKILL';
      const subNode = getOrCreateNode(skillNode.children!, `sub-${modId}-${skillId}-${subId}`, subId, 'subSkill');
      subNode.count++;
      // Level 4: Family
      const famId = q.familyId || 'UNKNOWN_FAMILY';
      const famNode = getOrCreateNode(subNode.children!, `fam-${modId}-${skillId}-${subId}-${famId}`, famId, 'family');
      famNode.count++;
      // Level 5: Question
      famNode.children!.push({
        id: `q-${q.id}`,
        label: q.question,
        type: 'question',
        count: 1,
        data: q,
        issues: issues.length > 0 ? issues : undefined
      });
    });
    // Sort the tree
    const sortNodes = (nodes: TreeNode[]) => {
      nodes.sort((a, b) => a.label.localeCompare(b.label));
      nodes.forEach(n => {
        if (n.children) sortNodes(n.children);
      });
    };
    sortNodes(root);
    // Analyze Duplicates (Same subSkill in multiple modules)
    const duplicateSkills: SimilarSkillGroup[] = [];
    subSkillMap.forEach((modules, subSkill) => {
      if (modules.size > 1) {
        duplicateSkills.push({
          name: subSkill,
          modules: Array.from(modules),
          count: subSkillCounts.get(subSkill) || 0
        });
      }
    });
    // Analyze Similar Skills (Substring matches or very close names)
    const similarSkills: {
      name1: string;
      name2: string;
      modules1: string[];
      modules2: string[];
    }[] = [];
    const subSkillsList = Array.from(subSkillMap.keys());
    for (let i = 0; i < subSkillsList.length; i++) {
      for (let j = i + 1; j < subSkillsList.length; j++) {
        const s1 = subSkillsList[i];
        const s2 = subSkillsList[j];
        // Skip if one is very short to avoid noise
        if (s1.length < 4 || s2.length < 4) continue;
        // Check if one contains the other
        if (s1.includes(s2) || s2.includes(s1)) {
          similarSkills.push({
            name1: s1,
            name2: s2,
            modules1: Array.from(subSkillMap.get(s1)!),
            modules2: Array.from(subSkillMap.get(s2)!)
          });
        }
        // Could add Levenshtein distance here for fuzzy matching
      }
    }
    // Analyze Hint Coverage
    const hintCoverage: HintCoverage[] = [];
    const orphanedHints: {
      moduleId: string;
      hintKey: string;
    }[] = [];
    // Check coverage: SubSkills -> Hints
    root.forEach(modNode => {
      const moduleId = modNode.label as ModuleId;
      const moduleHints = HINT_REGISTRY[moduleId];
      // Traverse to find subSkills in this module
      const findSubSkills = (node: TreeNode) => {
        if (node.type === 'subSkill') {
          const subSkill = node.label;
          // Check for direct match or partial match
          let hasHint = false;
          if (moduleHints) {
            if (moduleHints[subSkill]) hasHint = true;else {
              // Partial match check
              const keys = Object.keys(moduleHints);
              hasHint = keys.some(k => subSkill.includes(k) || k.includes(subSkill));
            }
          }
          hintCoverage.push({
            moduleId,
            subSkill,
            hasHint,
            questionCount: node.count
          });
        }
        if (node.children) node.children.forEach(findSubSkills);
      };
      if (modNode.children) modNode.children.forEach(findSubSkills);
    });
    // Check orphans: Hints -> SubSkills
    Object.entries(HINT_REGISTRY).forEach(([moduleId, hints]) => {
      Object.keys(hints).forEach(hintKey => {
        // Check if this hint key appears in any question's subSkill for this module
        const isUsed = questions.some(q => q.module === moduleId && q.subSkill && (q.subSkill === hintKey || q.subSkill.includes(hintKey) || hintKey.includes(q.subSkill)));
        if (!isUsed) {
          orphanedHints.push({
            moduleId,
            hintKey
          });
        }
      });
    });
    return {
      tree: root,
      stats: {
        modules: stats.modules.size,
        skills: stats.skills.size,
        subSkills: stats.subSkills.size,
        families: stats.families.size
      },
      unlinkedQuestions: unlinked,
      issueMap,
      duplicateSkills,
      similarSkills,
      hintCoverage,
      orphanedHints
    };
  }, [questions]);
  // Auto-expand modules on mount
  useEffect(() => {
    const initialExpanded = new Set<string>();
    tree.forEach(node => {
      if (node.type === 'module') {
        initialExpanded.add(node.id);
      }
    });
    setExpandedNodes(initialExpanded);
  }, [tree]);
  const toggleNode = (id: string) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedNodes(newExpanded);
  };
  const toggleIssueSection = (id: string) => {
    const newExpanded = new Set(expandedIssues);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedIssues(newExpanded);
  };
  const expandAll = () => {
    const allIds = new Set<string>();
    const traverse = (nodes: TreeNode[]) => {
      nodes.forEach(n => {
        allIds.add(n.id);
        if (n.children) traverse(n.children);
      });
    };
    traverse(tree);
    setExpandedNodes(allIds);
  };
  const collapseAll = () => {
    setExpandedNodes(new Set());
  };
  // Recursive Tree Renderer
  const renderNode = (node: TreeNode, depth: number = 0) => {
    const isExpanded = expandedNodes.has(node.id);
    const hasChildren = node.children && node.children.length > 0;
    const isQuestion = node.type === 'question';
    // Indentation
    const paddingLeft = `${depth * 1.5}rem`;
    // Icons based on type
    let Icon = Folder;
    let colorClass = 'text-slate-400';
    if (node.type === 'module') {
      Icon = BoxIcon;
      const mod = MODULES.find(m => m.id === node.label);
      if (mod) colorClass = mod.color;
    } else if (node.type === 'skill') {
      Icon = Layers;
      colorClass = 'text-indigo-500';
    } else if (node.type === 'subSkill') {
      Icon = Hash;
      colorClass = 'text-emerald-500';
    } else if (node.type === 'family') {
      Icon = Tag;
      colorClass = 'text-amber-500';
    } else if (node.type === 'question') {
      Icon = FileText;
      colorClass = 'text-slate-400';
    }
    if (isExpanded && !isQuestion) Icon = FolderOpen;
    // Check for issues in this node or children (simplified check)
    const nodeIssues = isQuestion ? issueMap.get(node.data!.id) : undefined;
    return <div key={node.id} className="select-none" data-id="element-794">
        <div className={`
            flex items-center gap-2 py-1.5 px-2 hover:bg-slate-50 rounded-lg cursor-pointer transition-colors
            ${isQuestion ? 'cursor-default hover:bg-transparent' : ''}
          `} style={{
        paddingLeft: depth === 0 ? '0.5rem' : paddingLeft
      }} onClick={() => !isQuestion && toggleNode(node.id)} data-id="element-795">
          {/* Expander Icon */}
          {!isQuestion && <div className="w-4 h-4 flex items-center justify-center text-slate-400" data-id="element-796">
              {hasChildren ? isExpanded ? <ChevronDown className="w-3 h-3" data-id="element-797" /> : <ChevronRight className="w-3 h-3" data-id="element-798" /> : <div className="w-3 h-3" data-id="element-799" />}
            </div>}
          {isQuestion && <div className="w-4 h-4" data-id="element-800" />} {/* Spacer */}
          {/* Type Icon */}
          <Icon className={`w-4 h-4 ${colorClass}`} data-id="element-801" />
          {/* Label */}
          <div className="flex-1 min-w-0 flex items-center gap-2" data-id="element-802">
            <span className={`text-sm truncate ${isQuestion ? 'text-slate-600 font-mono text-xs' : 'font-medium text-slate-700'}`} data-id="element-803">
              {node.type === 'question' ? <span className="flex items-center gap-2" data-id="element-804">
                  <span className="font-bold text-slate-900" data-id="element-805">
                    {node.id.replace('q-', '')}
                  </span>
                  <span className="text-slate-500 truncate max-w-[300px]" data-id="element-806">
                    {node.label}
                  </span>
                </span> : node.label}
            </span>

            {/* Count Badge */}
            {!isQuestion && <span className="text-xs text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded-full" data-id="element-807">
                {node.count}
              </span>}

            {/* Issues Badge */}
            {nodeIssues && <Badge variant="danger" size="sm" icon={<AlertTriangle className="w-3 h-3" data-id="element-809" />} data-id="element-808">
                {nodeIssues.length} issues
              </Badge>}
          </div>
        </div>

        {/* Children */}
        {isExpanded && hasChildren && <div className="border-l border-slate-100 ml-4" data-id="element-810">
            {node.children!.map(child => renderNode(child, depth + 1))}
          </div>}
      </div>;
  };
  return <div className="space-y-6" data-id="element-811">
      {/* Summary Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-id="element-812">
        <StatCard label="Modules" value={stats.modules} icon={<BoxIcon className="w-4 h-4 text-blue-500" data-id="element-814" />} data-id="element-813" />
        <StatCard label="Skills" value={stats.skills} icon={<Layers className="w-4 h-4 text-indigo-500" data-id="element-816" />} data-id="element-815" />
        <StatCard label="SubSkills" value={stats.subSkills} icon={<Hash className="w-4 h-4 text-emerald-500" data-id="element-818" />} data-id="element-817" />
        <StatCard label="Families" value={stats.families} icon={<Tag className="w-4 h-4 text-amber-500" data-id="element-820" />} data-id="element-819" />
        <StatCard label="Unlinked" value={unlinkedQuestions.length} icon={<AlertOctagon className="w-4 h-4 text-rose-500" data-id="element-822" />} isError={unlinkedQuestions.length > 0} data-id="element-821" />
      </div>

      {/* Main Content Area */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-823">
        {/* Tree Visualization */}
        <div className="lg:col-span-2 space-y-4" data-id="element-824">
          <div className="flex items-center justify-between" data-id="element-825">
            <h3 className="font-bold text-slate-900 flex items-center gap-2" data-id="element-826">
              <GitBranch className="w-5 h-5 text-indigo-600" data-id="element-827" />
              Taxonomy Tree
            </h3>
            <div className="flex gap-2" data-id="element-828">
              <button onClick={expandAll} className="text-xs font-medium text-indigo-600 hover:text-indigo-800 px-2 py-1 rounded hover:bg-indigo-50" data-id="element-829">
                Expand All
              </button>
              <button onClick={collapseAll} className="text-xs font-medium text-slate-500 hover:text-slate-700 px-2 py-1 rounded hover:bg-slate-50" data-id="element-830">
                Collapse All
              </button>
            </div>
          </div>

          <Card className="p-4 min-h-[500px] max-h-[800px] overflow-y-auto" data-id="element-831">
            {tree.length === 0 ? <div className="text-center py-12 text-slate-400" data-id="element-832">
                No data available
              </div> : <div className="space-y-1" data-id="element-833">
                {tree.map(node => renderNode(node))}
              </div>}
          </Card>
        </div>

        {/* Issues Panel */}
        <div className="space-y-4" data-id="element-834">
          <h3 className="font-bold text-slate-900 flex items-center gap-2" data-id="element-835">
            <AlertTriangle className="w-5 h-5 text-amber-500" data-id="element-836" />
            Integrity Issues
          </h3>

          <div className="space-y-3" data-id="element-837">
            {/* Section 1: Unlinked Questions */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-id="element-838">
              <button onClick={() => toggleIssueSection('unlinked')} className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 transition-colors" data-id="element-839">
                <div className="flex items-center gap-2" data-id="element-840">
                  <Link2Off className="w-4 h-4 text-rose-500" data-id="element-841" />
                  <span className="font-bold text-sm text-slate-700" data-id="element-842">
                    Ongekoppelde Vragen
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-843">
                  <Badge variant={unlinkedQuestions.length > 0 ? 'danger' : 'success'} size="sm" data-id="element-844">
                    {unlinkedQuestions.length}
                  </Badge>
                  {expandedIssues.has('unlinked') ? <ChevronDown className="w-4 h-4 text-slate-400" data-id="element-845" /> : <ChevronRight className="w-4 h-4 text-slate-400" data-id="element-846" />}
                </div>
              </button>

              <AnimatePresence data-id="element-847">
                {expandedIssues.has('unlinked') && <motion.div initial={{
                height: 0
              }} animate={{
                height: 'auto'
              }} exit={{
                height: 0
              }} className="overflow-hidden" data-id="element-848">
                    <div className="p-3 space-y-2 max-h-[300px] overflow-y-auto border-t border-slate-100" data-id="element-849">
                      {unlinkedQuestions.length === 0 ? <div className="text-center py-4 text-emerald-600 text-sm flex flex-col items-center" data-id="element-850">
                          <CheckCircle className="w-5 h-5 mb-1" data-id="element-851" />
                          Alles correct gekoppeld!
                        </div> : unlinkedQuestions.map(q => <div key={q.id} className="p-2 bg-rose-50 rounded border border-rose-100 text-xs" data-id="element-852">
                            <div className="font-mono font-bold text-rose-800 mb-1" data-id="element-853">
                              {q.id}
                            </div>
                            <div className="text-rose-700 mb-1 line-clamp-1" data-id="element-854">
                              {q.question}
                            </div>
                            <div className="flex flex-wrap gap-1" data-id="element-855">
                              {issueMap.get(q.id)?.map(issue => <span key={issue} className="px-1.5 py-0.5 bg-white rounded border border-rose-200 text-[10px] font-medium text-rose-600" data-id="element-856">
                                  {issue}
                                </span>)}
                            </div>
                          </div>)}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* Section 2: Duplicate & Similar Skills */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-id="element-857">
              <button onClick={() => toggleIssueSection('duplicates')} className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 transition-colors" data-id="element-858">
                <div className="flex items-center gap-2" data-id="element-859">
                  <Copy className="w-4 h-4 text-amber-500" data-id="element-860" />
                  <span className="font-bold text-sm text-slate-700" data-id="element-861">
                    Dubbele & Vergelijkbare Skills
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-862">
                  <Badge variant={duplicateSkills.length + similarSkills.length > 0 ? 'warning' : 'success'} size="sm" data-id="element-863">
                    {duplicateSkills.length + similarSkills.length}
                  </Badge>
                  {expandedIssues.has('duplicates') ? <ChevronDown className="w-4 h-4 text-slate-400" data-id="element-864" /> : <ChevronRight className="w-4 h-4 text-slate-400" data-id="element-865" />}
                </div>
              </button>

              <AnimatePresence data-id="element-866">
                {expandedIssues.has('duplicates') && <motion.div initial={{
                height: 0
              }} animate={{
                height: 'auto'
              }} exit={{
                height: 0
              }} className="overflow-hidden" data-id="element-867">
                    <div className="p-3 space-y-3 max-h-[300px] overflow-y-auto border-t border-slate-100" data-id="element-868">
                      {duplicateSkills.length === 0 && similarSkills.length === 0 ? <div className="text-center py-4 text-emerald-600 text-sm flex flex-col items-center" data-id="element-869">
                          <CheckCircle className="w-5 h-5 mb-1" data-id="element-870" />
                          Geen dubbele skills gevonden
                        </div> : <>
                          {duplicateSkills.map((dup, i) => <div key={`dup-${i}`} className="p-2 bg-amber-50 rounded border border-amber-100 text-xs" data-id="element-871">
                              <div className="flex items-center gap-1 mb-1" data-id="element-872">
                                <AlertTriangle className="w-3 h-3 text-amber-600" data-id="element-873" />
                                <span className="font-bold text-amber-800" data-id="element-874">
                                  Dubbele SubSkill
                                </span>
                              </div>
                              <div className="font-mono text-slate-700 mb-1" data-id="element-875">
                                "{dup.name}"
                              </div>
                              <div className="text-slate-500 mb-1" data-id="element-876">
                                Komt voor in modules:
                              </div>
                              <div className="flex flex-wrap gap-1" data-id="element-877">
                                {dup.modules.map(m => <Badge key={m} variant="neutral" size="sm" data-id="element-878">
                                    {m}
                                  </Badge>)}
                              </div>
                            </div>)}
                          {similarSkills.map((sim, i) => <div key={`sim-${i}`} className="p-2 bg-blue-50 rounded border border-blue-100 text-xs" data-id="element-879">
                              <div className="flex items-center gap-1 mb-1" data-id="element-880">
                                <Search className="w-3 h-3 text-blue-600" data-id="element-881" />
                                <span className="font-bold text-blue-800" data-id="element-882">
                                  Vergelijkbare Namen
                                </span>
                              </div>
                              <div className="grid grid-cols-2 gap-2 mt-1" data-id="element-883">
                                <div data-id="element-884">
                                  <div className="font-mono text-slate-700" data-id="element-885">
                                    {sim.name1}
                                  </div>
                                  <div className="text-[10px] text-slate-400" data-id="element-886">
                                    {sim.modules1.join(', ')}
                                  </div>
                                </div>
                                <div data-id="element-887">
                                  <div className="font-mono text-slate-700" data-id="element-888">
                                    {sim.name2}
                                  </div>
                                  <div className="text-[10px] text-slate-400" data-id="element-889">
                                    {sim.modules2.join(', ')}
                                  </div>
                                </div>
                              </div>
                            </div>)}
                        </>}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* Section 3: Hint Coverage */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-id="element-890">
              <button onClick={() => toggleIssueSection('hints')} className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 transition-colors" data-id="element-891">
                <div className="flex items-center gap-2" data-id="element-892">
                  <BookOpen className="w-4 h-4 text-indigo-500" data-id="element-893" />
                  <span className="font-bold text-sm text-slate-700" data-id="element-894">
                    Hint Dekking
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-895">
                  <Badge variant="neutral" size="sm" data-id="element-896">
                    {Math.round(hintCoverage.filter(h => h.hasHint).length / Math.max(1, hintCoverage.length) * 100)}
                    %
                  </Badge>
                  {expandedIssues.has('hints') ? <ChevronDown className="w-4 h-4 text-slate-400" data-id="element-897" /> : <ChevronRight className="w-4 h-4 text-slate-400" data-id="element-898" />}
                </div>
              </button>

              <AnimatePresence data-id="element-899">
                {expandedIssues.has('hints') && <motion.div initial={{
                height: 0
              }} animate={{
                height: 'auto'
              }} exit={{
                height: 0
              }} className="overflow-hidden" data-id="element-900">
                    <div className="p-3 space-y-2 max-h-[300px] overflow-y-auto border-t border-slate-100" data-id="element-901">
                      {/* Uncovered SubSkills */}
                      {hintCoverage.filter(h => !h.hasHint).map((h, i) => <div key={`no-hint-${i}`} className="flex items-center justify-between p-2 bg-slate-50 rounded border border-slate-100 text-xs" data-id="element-902">
                            <div data-id="element-903">
                              <div className="font-medium text-slate-700" data-id="element-904">
                                {h.subSkill}
                              </div>
                              <div className="text-[10px] text-slate-400" data-id="element-905">
                                {h.moduleId} • {h.questionCount} vragen
                              </div>
                            </div>
                            <Badge variant="warning" size="sm" data-id="element-906">
                              Geen Hint
                            </Badge>
                          </div>)}

                      {/* Orphaned Hints */}
                      {orphanedHints.map((h, i) => <div key={`orphan-${i}`} className="flex items-center justify-between p-2 bg-slate-50 rounded border border-slate-100 text-xs opacity-60" data-id="element-907">
                          <div data-id="element-908">
                            <div className="font-medium text-slate-700" data-id="element-909">
                              {h.hintKey}
                            </div>
                            <div className="text-[10px] text-slate-400" data-id="element-910">
                              {h.moduleId} • Hint bestaat, geen vragen
                            </div>
                          </div>
                          <Badge variant="neutral" size="sm" data-id="element-911">
                            Ongebruikt
                          </Badge>
                        </div>)}

                      {hintCoverage.filter(h => !h.hasHint).length === 0 && orphanedHints.length === 0 && <div className="text-center py-4 text-emerald-600 text-sm flex flex-col items-center" data-id="element-912">
                            <CheckCircle className="w-5 h-5 mb-1" data-id="element-913" />
                            Perfecte hint dekking!
                          </div>}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
function StatCard({
  label,
  value,
  icon,
  isError = false
}: any) {
  return <div className={`p-4 rounded-xl border ${isError ? 'bg-rose-50 border-rose-200' : 'bg-white border-slate-200'}`} data-id="element-914">
      <div className="flex items-center justify-between mb-1" data-id="element-915">
        <span className={`text-xs font-bold uppercase tracking-wider ${isError ? 'text-rose-600' : 'text-slate-400'}`} data-id="element-916">
          {label}
        </span>
        {icon}
      </div>
      <div className={`text-2xl font-bold ${isError ? 'text-rose-900' : 'text-slate-900'}`} data-id="element-917">
        {value}
      </div>
    </div>;
}